import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Property {
  id: string;
  title: string;
  price: number;
  sale_price?: number;
  rent_price?: number;
  currency?: string;
  location: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  property_type: string;
  status: string;
  available_for_sale?: boolean;
  available_for_rent?: boolean;
  images: string[];
  description: string;
  city: string;
  is_featured?: boolean;
}

export default function OficinasEnRenta() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOffices();
  }, []);

  const fetchOffices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ action: 'all' })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Filtrar solo oficinas en renta
        const offices = (data.properties || [])
          .filter((prop: any) => 
            prop.property_type === 'oficina' && 
            prop.available_for_rent === true &&
            prop.status === 'available'
          )
          .map((prop: any) => ({
            id: prop.id,
            title: prop.title || 'Oficina sin título',
            price: parseFloat(prop.price) || 0,
            sale_price: prop.sale_price ? parseFloat(prop.sale_price) : null,
            rent_price: prop.rent_price ? parseFloat(prop.rent_price) : null,
            currency: prop.currency || 'MXN',
            location: prop.location || prop.city || 'Ubicación no especificada',
            bedrooms: prop.bedrooms || 0,
            bathrooms: prop.bathrooms || 0,
            area: parseFloat(prop.area) || 0,
            property_type: prop.property_type || 'oficina',
            status: prop.status || 'available',
            available_for_sale: prop.available_for_sale || false,
            available_for_rent: prop.available_for_rent || false,
            images: Array.isArray(prop.images) ? prop.images : [],
            description: prop.description || 'Sin descripción disponible',
            city: prop.city || '',
            is_featured: prop.is_featured || false
          }));
        
        setProperties(offices);
      }
    } catch (error) {
      console.error('Error fetching offices:', error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number, currency: string = 'MXN') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    } else {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    }
  };

  // Schema.org para la página de landing
  const landingSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Oficinas en Renta - GRUPO FR Venture",
    "description": "Oficinas en renta en México. Encuentra oficinas AAA, ejecutivas y corporativas en las mejores ubicaciones de Mérida, Ciudad de México y más. Desde 50m² hasta 1000m², con estacionamiento, seguridad 24/7 y servicios incluidos.",
    "url": "https://grupofrventure.com/oficinas-en-renta",
    "provider": {
      "@type": "RealEstateAgent",
      "name": "GRUPO FR Venture",
      "url": "https://grupofrventure.com"
    },
    "about": {
      "@type": "Service",
      "serviceType": "Renta de Oficinas",
      "areaServed": [
        {
          "@type": "City",
          "name": "Mérida",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Mérida",
            "addressRegion": "Yucatán",
            "addressCountry": "MX"
          }
        },
        {
          "@type": "City",
          "name": "Ciudad de México",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Ciudad de México",
            "addressRegion": "CDMX",
            "addressCountry": "MX"
          }
        }
      ]
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando oficinas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(landingSchema)
        }}
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <a href="/">
                <img 
                  src="https://static.readdy.ai/image/05c782bef5e03af806ba1d51091d6c47/3aa9c4448526bbcc8377a061d058f682.png" 
                  alt="GRUPO FR" 
                  className="h-16 w-auto object-contain cursor-pointer"
                />
              </a>
            </div>
            <nav className="flex items-center space-x-8">
              <a href="/" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">
                Inicio
              </a>
              <a href="/#propiedades" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">
                Todas las Propiedades
              </a>
              <a href="/#contacto" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">
                Contacto
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              Oficinas en Renta en México
            </h1>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Encuentra la oficina perfecta para tu negocio. Contamos con oficinas AAA, ejecutivas y corporativas 
              en las mejores ubicaciones de Mérida, Ciudad de México y más ciudades. Desde 50m² hasta 1000m², 
              con estacionamiento, seguridad 24/7, internet de alta velocidad y servicios incluidos.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <i className="ri-building-line"></i>
                <span>Oficinas AAA</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <i className="ri-map-pin-line"></i>
                <span>Mejores Ubicaciones</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <i className="ri-parking-box-line"></i>
                <span>Estacionamiento Incluido</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <i className="ri-shield-check-line"></i>
                <span>Seguridad 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Oficinas Disponibles */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              {properties.length} Oficinas Disponibles en Renta
            </h2>
            <p className="text-xl text-gray-600">
              Oficinas listas para ocupación inmediata en las mejores zonas corporativas
            </p>
          </div>

          {properties.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-building-line text-4xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay oficinas disponibles en este momento
              </h3>
              <p className="text-gray-500 mb-6">
                Estamos actualizando nuestro inventario constantemente
              </p>
              <a 
                href="/#contacto"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
              >
                Contáctanos para más información
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property) => (
                <div 
                  key={property.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-64">
                    <img
                      src={property.images[0] || 'https://public.readdy.ai/ai/img_res/0e608da1bf7d6c97c15b8b02032f6e68.jpg'}
                      alt={property.title}
                      className="w-full h-full object-cover object-top"
                    />
                    {property.is_featured && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-semibold">
                          <i className="ri-star-line mr-1"></i>
                          Destacada
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{property.title}</h3>
                    
                    <div className="flex items-center text-gray-600 mb-4">
                      <i className="ri-map-pin-line mr-2"></i>
                      <span className="text-sm">{property.location}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                      <div className="flex items-center">
                        <i className="ri-square-line mr-1"></i>
                        <span>{property.area} m²</span>
                      </div>
                      {property.bathrooms > 0 && (
                        <div className="flex items-center">
                          <i className="ri-drop-line mr-1"></i>
                          <span>{property.bathrooms} baños</span>
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <div className="text-2xl font-bold text-blue-600">
                        {formatPrice(property.rent_price || property.price, property.currency)}/mes
                      </div>
                    </div>

                    <a
                      href="/#propiedades"
                      className="block w-full bg-blue-600 text-white text-center px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer"
                    >
                      Ver Detalles
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Por qué elegir nuestras oficinas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              ¿Por Qué Elegir Nuestras Oficinas?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Edificios AAA</h3>
              <p className="text-gray-600 text-sm">
                Oficinas en edificios de primera categoría con acabados de lujo
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Ubicaciones Premium</h3>
              <p className="text-gray-600 text-sm">
                En las mejores zonas corporativas y comerciales de cada ciudad
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-service-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Servicios Incluidos</h3>
              <p className="text-gray-600 text-sm">
                Internet, agua, luz, mantenimiento y más servicios incluidos
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Seguridad Total</h3>
              <p className="text-gray-600 text-sm">
                Vigilancia 24/7, acceso controlado y sistemas de seguridad
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            ¿Listo para Encontrar tu Oficina Ideal?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Contáctanos hoy y te ayudaremos a encontrar la oficina perfecta para tu negocio
          </p>
          <a
            href="/#contacto"
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer"
          >
            Contactar Ahora
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <img 
                src="https://static.readdy.ai/image/05c782bef5e03af806ba1d51091d6c47/3aa9c4448526bbcc8377a061d058f682.png" 
                alt="GRUPO FR" 
                className="h-16 w-auto object-contain brightness-0 invert"
              />
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-gray-400">
                © 2024 GRUPO FR. Todos los derechos reservados.
              </p>
              <div className="flex items-center justify-center md:justify-end gap-4 text-gray-400 text-sm mt-1">
                <a 
                  href="https://readdy.ai/?origin=logo" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-white transition-colors"
                >
                  Website Builder
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
